import React from 'react'
import { CheckSquare, Calendar, Flag, Clock, MessageSquare } from 'lucide-react'
import { useTasks } from '../../hooks/useTasks'
import './TaskDetailsWidget.scss'

interface TaskDetailsWidgetProps {
  lifeSpace?: 'personal' | 'work' | 'home' | 'all'
}

export const TaskDetailsWidget: React.FC<TaskDetailsWidgetProps> = ({ lifeSpace }) => {
  const { selectedTask, toggleTaskComplete, toggleSubtaskComplete } = useTasks({ lifeSpace })

  const getAttentionColor = (attention: string) => {
    switch (attention) {
      case 'high': return 'task-details-widget__priority--high'
      case 'medium': return 'task-details-widget__priority--medium'
      case 'low': return 'task-details-widget__priority--low'
      default: return ''
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  if (!selectedTask) {
    return (
      <div className="task-details-widget">
        <div className="task-details-widget__header">
          <CheckSquare size={16} />
          <span className="task-details-widget__title">Aufgabe Details</span>
        </div>
        <div className="task-details-widget__empty">
          <span>Keine Aufgabe ausgewählt</span>
        </div>
      </div>
    )
  }

  const completedSubtasks = selectedTask.subtasks?.filter(st => st.completed).length || 0
  const totalSubtasks = selectedTask.subtasks?.length || 0
  const progress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0

  return (
    <div className="task-details-widget">
      <div className="task-details-widget__header">
        <CheckSquare size={16} />
        <span className="task-details-widget__title">Aufgabe Details</span>
      </div>

      <div className="task-details-widget__task-title">
        <input
          type="checkbox"
          checked={selectedTask.completed}
          className="task-details-widget__checkbox"
          onChange={() => toggleTaskComplete(selectedTask.id)}
        />
        <span className={selectedTask.completed ? 'task-details-widget__task-title--completed' : ''}>
          {selectedTask.title}
        </span>
      </div>

      <div className="task-details-widget__description">
        {selectedTask.description}
      </div>

      <div className="task-details-widget__meta">
        <div className="task-details-widget__meta-item">
          <Calendar size={12} />
          <span>{formatDate(selectedTask.dueDate)}</span>
        </div>
        {selectedTask.attention && (
          <div className={`task-details-widget__meta-item task-details-widget__priority ${getAttentionColor(selectedTask.attention)}`}>
            <Flag size={12} />
            <span>{selectedTask.attention === 'high' ? 'Hoch' : selectedTask.attention === 'medium' ? 'Mittel' : 'Niedrig'}</span>
          </div>
        )}
      </div>

      <div className="task-details-widget__progress">
        <div className="task-details-widget__progress-header">
          <span>Unteraufgaben</span>
          <span>{completedSubtasks}/{totalSubtasks}</span>
        </div>
        <div className="task-details-widget__progress-bar">
          <div
            className="task-details-widget__progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {selectedTask.subtasks && selectedTask.subtasks.length > 0 && (
        <div className="task-details-widget__subtasks">
          {selectedTask.subtasks.map(subtask => (
            <div key={subtask.id} className="task-details-widget__subtask">
              <input
                type="checkbox"
                checked={subtask.completed}
                className="task-details-widget__subtask-checkbox"
                onChange={() => toggleSubtaskComplete(selectedTask.id, subtask.id)}
              />
              <span className={subtask.completed ? 'task-details-widget__subtask--completed' : ''}>
                {subtask.title}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="task-details-widget__footer">
        <button className="task-details-widget__action">
          <MessageSquare size={12} />
          <span>{selectedTask.comments?.length || 0} Kommentare</span>
        </button>
        <button className="task-details-widget__action">
          <Clock size={12} />
          <span>Bearbeiten</span>
        </button>
      </div>
    </div>
  )
}
